import { memo } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { IconMap } from "../questionTypes";
import { setQuestionModalDetails } from "../../../Redux/reducers/questionTranslationSlice";
import { ErrorOutline } from "@mui/icons-material";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import {
  QuestionItemStyle,
  QuestionDescriptorStyle,
  requiredStyles,
} from "../question.style";
import { translateQuestionSkeleton } from "../../../utils/question";
import { EditIcon } from "../Icons";

type Props = {
  question: any;
  index: number;
};

const QuestionItemWrapper: React.FC<Props> = ({ question, index }) => {
  const dispatch = useAppDispatch();

  const editable = useAppSelector(
    (state) => state.questionTranslation.editable
  );

  const Icon = IconMap[question.type];

  const isTranslated = question?.translations?.length > 0;
  const isDisplayNumber =
    question?.translations?.[0]?.properties?.displayNumber;

  const handleEditQuestion = () => {
    if (isTranslated) {
      let updatedChoices = question?.choices?.map((choice: any) => {
        const translatedChoice = question.translations[0].choices.find(
          (c: any) => c.questionChoiceId === choice.id
        );

        if (translatedChoice) {
          return {
            questionChoiceId: choice.id,
            header: choice?.label,
            label: translatedChoice?.label,
            imageUrl: translatedChoice?.imageUrl,
            previewUrl: translatedChoice?.previewUrl,
            position: choice?.position,
          };
        } else {
          return {
            questionChoiceId: choice.id,
            label: "",
            imageUrl: null,
            header: choice?.label,
            position: choice?.position,
          };
        }
      });
      const sortedChoices = updatedChoices.sort(
        (a: any, b: any) => a.position - b.position
      );

      let updatedTranslateQuestion = {
        ...question.translations[0],
        choices: sortedChoices,
        type: question.type,
      };

      dispatch(
        setQuestionModalDetails({
          question: updatedTranslateQuestion,
          show: true,
          mainQuestion: {
            showIndex: question.properties.showIndex,
            allowRemark: question.properties.remarkText ? true : false,
          },
        })
      );
    } else {
      let newTranslation: any = translateQuestionSkeleton(question.type);

      if (
        question.type === "checkbox" ||
        question.type === "radio" ||
        question.type === "dropdown"
      ) {
        let choices = question?.choices.map((choice: any) => {
          return {
            questionChoiceId: choice.id,
            label: "",
            imageUrl: null,
            header: choice.label,
          };
        });
        newTranslation.choices = choices;
      }

      let updatedTranslation = {
        ...newTranslation,
        formId: question.formId,
        questionId: question.id,
      };

      dispatch(
        setQuestionModalDetails({
          question: updatedTranslation,
          show: true,
          mainQuestion: {
            showIndex: question.properties.showIndex,
            allowRemark: question.properties.remarkText ? true : false,
          },
        })
      );
    }
  };

  return (
    <Stack sx={QuestionItemStyle} justifyContent={"center"} gap={2}>
      <Stack
        direction="row"
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <Stack direction="row" alignItems={"flex-start"}>
          <Stack
            sx={QuestionDescriptorStyle}
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            flexShrink={0}
          >
            <Typography fontWeight={"regular"} variant="subtitle1">
              {isDisplayNumber
                ? question.translations?.[0].properties?.displayNumber
                : question?.properties?.displayNumber
                ? question?.properties?.displayNumber
                : index + 1}
            </Typography>
            {Icon && <Icon />}
          </Stack>

          <Box sx={{ flex: 1, minWidth: "0px" }}>
            {/* <Typography
              fontSize={16}
              fontWeight={500}
              sx={question?.properties?.required ? requiredStyles : {}}
            >
              {question.label}
            </Typography> */}
            <Box sx={question?.properties?.required ? requiredStyles : {}}>
              <MDEditor.Markdown
                wrapperElement={{
                  "data-color-mode": "light",
                }}
                source={
                  isTranslated
                    ? question.translations[0].label
                    : question.labelMarkdown
                }
                rehypePlugins={[rehypeSanitize]}
                style={{ fontWeight: "600", fontSize: "14px" }}
              />
            </Box>

            <Typography
              variant="body2"
              fontWeight={"regular"}
              sx={{ color: "text.secondary" }}
              mt={1}
            >
              ({question.varname})
              {question?.dependency && (
                <DependencyTextPreview dep={question?.dependency} />
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems={"center"}>
          {!question?.translations?.[0]?.isComplete && (
            <Tooltip title={"Add Translations"}>
              <IconButton>
                <ErrorOutline color="warning" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={editable ? "Edit" : "View Details"}>
            <IconButton
              onClick={handleEditQuestion}
              sx={{ height: 40, width: 40 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default memo(QuestionItemWrapper);

const symbolMap: Record<string, string> = {
  eq: "is equal to",
  not_eq: "is not",
  gt: "greater than",
  lt: "less than",
  gte: "greater than or equal to",
  lte: "less than or equal to",
  is_checked: "is checked",
  is_not_checked: "is not checked",
};

const keyMap: Record<string, string> = {
  text: "textValue",
  date: "textValue",
  number: "numberValue",
  slider: "numberValue",
  calculated_field: "numberValue",
  calculated_field_text: "textValue",
  radio: "questionChoice",
  dropdown: "questionChoice",
  checkbox: "questionChoice",
  randomize: "textValue",
};

const DependencyTextPreview = ({ dep }: { dep: any }) => {
  const dependent = dep?.parentQuestion?.varname;
  const operator = symbolMap[dep?.operator];
  let type = dep?.parentQuestion?.type;
  if (
    type === "calculated_field" &&
    dep?.parentQuestion?.properties?.responseType === "string"
  ) {
    type = "calculated_field_text";
  }
  const label = ["radio", "dropdown", "checkbox"].includes(type)
    ? dep?.[keyMap[type]]?.label
    : dep?.[keyMap[type]];

  return (
    <>
      {type === "checkbox" ? (
        <Typography
          variant="body2"
          fontWeight={"regular"}
          sx={{ color: "text.secondary" }}
          component={"span"}
        >
          &nbsp;&nbsp;|&nbsp;&nbsp; ({label} in {dependent}
          &nbsp;
          <strong>{operator}</strong>)
        </Typography>
      ) : (
        <Typography
          variant="body2"
          fontWeight={"regular"}
          sx={{ color: "text.secondary" }}
          component={"span"}
        >
          &nbsp;&nbsp;|&nbsp;&nbsp; ({dependent} <strong>{operator}</strong>{" "}
          {label})
        </Typography>
      )}
    </>
  );
};
